import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";

// store
import { dbDataStore, messageAndErrorStore, userStore } from "../../store/Store";

// components
import InputField from "../../formComponents/InputField";
import FormButton from "../../formComponents/FormButton";
import DropDown from "../../formComponents/DropDown";

// css and images
import Refresh from "../../assets/refresh.svg";
import UpdatePopup from "../../components/UpdatePopup";

// data
import { billingPrefrenceOptions } from "../../data/collections-data";

export default function CompanyGeneralSettings() {
  // global
  const currentCompany = userStore(
    (userDataStore) => userDataStore.currentCompany,
  );
  const currentUser = userStore((userDataStore) => userDataStore.currentUser);
  const setCurrentCompany = userStore(
    (userDataStore) => userDataStore.setCurrentCompany,
  );
  const globalCompanyNames = dbDataStore((dbData) => dbData.globalCompanyNames);
  const JwtToken = userStore((userDataStore) => userDataStore.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  const [pending, setPending] = useState(false);
  const [trigger, setTrigger] = useState(false);
  const currentName = currentCompany.company_name;
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCurrentCompany({
      ...currentCompany,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelect = (o, e) => {
    setCurrentCompany({
      ...currentCompany,
      [e.name]: o.value,
    });
  };

  const handleToggle = (_, newAlignment) => {
    if (newAlignment === "yes") {
      setCurrentCompany({
        ...currentCompany,
        customer_approval_required: true,
      });
    } else {
      setCurrentCompany({
        ...currentCompany,
        customer_approval_required: false,
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const idx = globalCompanyNames.indexOf(currentCompany.company_name);

    if (idx !== -1 && currentName !== currentCompany.company_name) {
      toggleNewMessage("That company already exists");
      return;
    }
    const body = currentCompany;
    body.updated_by_id = currentUser.id;
    setPending(true);
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method: "PATCH",
      headers,
      body: JSON.stringify(currentCompany),
      signal: controller.signal,
    };
    
    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/company/${currentCompany.id}`,
      requestOptions,
    )
      .then((res) => {
        if (res.status === 401) {
          setAuthStatus(false);
          setPending(false);
          navigate("/login");
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          toggleNewMessage("Something went wrong");
          setPending(false);
        } else {
          setCurrentCompany(data);
          toggleNewMessage(null, "Company updated");
          setPending(false);
        }
      })
      .catch((err) => {
        toggleNewMessage("Something went wrong");
        setPending(false);
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  return (
    currentCompany && (
      <div className="container-fluid">
        {!currentCompany && (
          <p className="error">Could not retrieve the company data</p>
        )}
        {currentCompany && (
          <div className="user-profile">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-5">
                  <InputField
                    name={"company_name"}
                    title={"Company Name"}
                    className={"form-control"}
                    divClass={"mb-3"}
                    type={"text"}
                    placeholder={"Company Name"}
                    onChange={handleChange}
                    value={currentCompany.company_name}
                    required={true}
                  />
                </div>
              </div>
              <div className="row g-3">
                {currentCompany.full_access && (
                  <div className="col-6">
                    <InputField
                      name={"api_key"}
                      title={"PropConnect API Key"}
                      className={"form-control"}
                      divClass={"mb-3"}
                      type={"text"}
                      placeholder={"API Key"}
                      onChange={handleChange}
                      value={currentCompany.api_key}
                    />
                  </div>
                )}
                <div className="row-auto">
                  <div className="col-6">
                    {currentCompany.full_access && (
                      <div>
                        <DropDown
                          classOne={"mb-1"}
                          classTwo="form-label bold-label"
                          title="Billing Prefrence"
                          name={"billing_preference_type"}
                          options={billingPrefrenceOptions}
                          onChange={handleSelect}
                          placeholder={"Select Preference"}
                          required={true}
                          value={
                            billingPrefrenceOptions.filter(
                              (x) =>
                                x.value ===
                                currentCompany.billing_preference_type,
                            )[0]
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
                {currentCompany.full_access && (
                  <div className="mb-1 g-3">
                    <label className="form-label fw-bold">
                      Customer Approval Required
                    </label>
                    <div className="col-5">
                      <ToggleButtonGroup
                        value={
                          currentCompany.customer_approval_required
                            ? "yes"
                            : "no"
                        }
                        exclusive
                        onChange={handleToggle}
                        aria-label="CustomerApprovalRequired"
                        size="small"
                      >
                        <ToggleButton
                          value="yes"
                          color="success"
                          id={currentCompany.id}
                          sx={{ borderRadius: "20px" }}
                        >
                          Yes
                        </ToggleButton>
                        <ToggleButton
                          value="no"
                          color="warning"
                          id={currentCompany.id}
                          sx={{ borderRadius: "20px" }}
                        >
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  </div>
                )}
              </div>
              <FormButton pending={pending} name={"Update Settings"} />
            </form>
            <label className="form-label bold-label">qwikr API Key</label>
            <div className="d-flex">
              <div className="col-6">
                <input
                  name={"qwikr_company_key"}
                  title={"qwikr key"}
                  className={"form-control"}
                  type={"text"}
                  placeholder={"qiwkr Key"}
                  value={currentCompany.qwikr_company_key || ""}
                  readOnly
                />
              </div>
              <img
                src={Refresh}
                alt="refresh"
                onClick={() => setTrigger(true)}
                className="action-img"
              />
            </div>
          </div>
        )}
        {trigger && (
          <UpdatePopup
            setTrigger={setTrigger}
            url={`${process.env.REACT_APP_BACKEND}/pc/company/key`}
            method={"PATCH"}
            title={"Are you sure you want to update the qwikr key"}
            errorMessage={"Could not update the qwikry key"}
            successMessage={"qwikr key updated"}
            setState={setCurrentCompany}
          />
        )}
      </div>
    )
  );
}
